/**
 * Proformas/Quotations Page
 *
 * Mobile-first proforma list with cards on mobile, table on desktop.
 * Follows the same pattern as BillsPage.
 */

import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, FileText, ChevronRight } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, debounce } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'

interface Proforma {
  id: number
  bill_number: string | null
  client_name: string | null
  notes?: string | null
  created_at: string
  total: number
  status: string
}

export function ProformasPage() {
  const navigate = useNavigate()
  const [page, setPage] = useUrlPage()
  const [searchQuery, setSearchQuery] = useUrlState('q')
  const [searchInput, setSearchInput] = useState(searchQuery)

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Fetch proformas (bills with type 'proforma').
  // staleTime=0 + refetchOnMount: la lista es chica y dinamica (cualquier
  // creacion/edicion/conversion la modifica), preferimos refetchear siempre
  // antes que andar invalidando en cada mutacion nueva.
  const { data, isLoading } = useQuery({
    queryKey: ['proformas', page, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      params.set('bill_type', 'proforma')
      if (searchQuery) {
        params.set('search', searchQuery)
      }
      const response = await api.get(`/bills/?${params}`)
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">{t('proformas.title')}</h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.total || 0} {t('proformas.quotations')}
          </p>
        </div>
        <Button asChild className="hidden md:flex">
          <Link to="/bills/new?type=proforma">
            <Plus className="h-4 w-4 mr-2" />
            {t('proformas.newProforma')}
          </Link>
        </Button>
      </div>

      {/* Search - Touch friendly */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('proformas.searchPlaceholder')}
          className="pl-10 h-12 text-base"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('proformas.noProformas')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery ? t('common.noResults') : t('proformas.addFirst')}
          </p>
          {!searchQuery && (
            <Button asChild>
              <Link to="/bills/new?type=proforma">
                <Plus className="h-4 w-4 mr-2" />
                {t('proformas.newProforma')}
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards - No buttons, just click to view */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((proforma: Proforma) => (
              <Link
                key={proforma.id}
                to={`/proformas/${proforma.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card
                className="active:bg-muted/50 cursor-pointer touch-manipulation"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">
                          PRO-{proforma.bill_number || proforma.id}
                        </p>
                        <Badge variant="secondary">{t('proformas.quotation')}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {proforma.client_name || t('bills.generalClient')}
                      </p>
                      {proforma.notes && (
                        <p className="text-xs text-muted-foreground italic break-words mt-0.5">
                          &ldquo;{proforma.notes}&rdquo;
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDate(proforma.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="font-bold text-primary text-lg">
                        {formatCurrency(proforma.total)}
                      </p>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">{t('proformas.proformaNumber')}</th>
                    <th className="text-left p-4 font-medium">{t('bills.client')}</th>
                    <th className="text-left p-4 font-medium">{t('bills.date')}</th>
                    <th className="text-right p-4 font-medium">{t('bills.total')}</th>
                    <th className="text-left p-4 font-medium">{t('bills.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((proforma: Proforma) => (
                    <tr
                      key={proforma.id}
                      className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => navigate(`/proformas/${proforma.id}`)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`/proformas/${proforma.id}`)
                      }}
                    >
                      <td className="p-4 font-medium">
                        PRO-{proforma.bill_number || proforma.id}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span>{proforma.client_name || t('bills.generalClient')}</span>
                          {proforma.notes && (
                            <span className="text-xs text-muted-foreground italic">
                              &ldquo;{proforma.notes}&rdquo;
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">{formatDate(proforma.created_at)}</td>
                      <td className="p-4 text-right font-medium text-primary">
                        {formatCurrency(proforma.total)}
                      </td>
                      <td className="p-4">
                        <Badge variant="secondary">{t('proformas.quotation')}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
